import type { TFunction } from 'i18next';
import { DATE_FORMAT, formatDate } from '../../lib/date/formatDate';
import { createLabelResolver } from '../../lib/labeledCode';
import type { TableColumn } from '../ui/tableTemplate/TableTemplateTypes';
import type {
  BooleanFilterValue,
  DateFilterValue,
  DateRangeFilterValue,
  FilterOp,
  FilterState,
  FilterValue,
  ListFilterValue,
  MonthYearFilterValue,
  NumericFilterValue,
  TextFilterValue,
} from '../ui/tableTemplate/TableTemplateTypes';
import { FILTER_OPERATOR_OPTIONS } from './filterTypes';
import type { ListFilterDef } from './filterTypes';

/** Compact operator glyphs for chip descriptions — derived from the single
 *  operator source (`FILTER_OPERATOR_OPTIONS`) so symbols never drift. */
const OP_SYMBOL = Object.fromEntries(
  FILTER_OPERATOR_OPTIONS.map(o => [o.value, o.label]),
) as Record<FilterOp, string>;

/** List glyphs — reuse the comparison symbols so chips read like the other
 *  filters (in → '=', not_in → '≠') rather than words or set notation. */
const LIST_GLYPH: Record<'in' | 'not_in', string> = { in: OP_SYMBOL.eq, not_in: OP_SYMBOL.ne };


export interface FilterChip<T> {
  key: keyof T;
  /** Human-readable "Label op value(s)" summary, e.g. "Code terrain ∈ Gao, Mopti". */
  text: string;
}

/** Everything a describer might need beyond the value itself. */
interface DescribeContext {
  col: TableColumn<unknown>;
  t: TFunction;
}

/** Renders one filter value as "op value(s)" (no field label). */
type FilterDescriber<V extends FilterValue = FilterValue> = (
  value: V,
  ctx: DescribeContext,
) => string;

/** Open-ended ranges (only one bound filled) read as a single comparison,
 *  matching how the equivalent `date` + gte/lte filter renders. */
export function describeDateRange(value: DateRangeFilterValue): string {
  const from = value.from ? formatDate(value.from, DATE_FORMAT) : '';
  const to = value.to ? formatDate(value.to, DATE_FORMAT) : '';
  if (from && to) return `${from} – ${to}`;
  if (from) return `${OP_SYMBOL.gte} ${from}`;
  if (to) return `${OP_SYMBOL.lte} ${to}`;
  return '';
}

function describeList(value: ListFilterValue, { col, t }: DescribeContext): string {
  const listDef = col.filter?.kind === 'list' ? (col.filter as ListFilterDef) : undefined;
  const resolve = createLabelResolver(
    listDef?.options ?? [],
    t as unknown as (key: string, fallback: string) => string,
    listDef?.optionLabelPrefix,
  );
  return `${LIST_GLYPH[value.op]} ${value.values.map(v => resolve(v)).join(', ')}`;
}

/**
 * One describer per filter kind. `Record<FilterValue['kind'], …>` makes the set
 * exhaustive — adding a new kind is a compile error until it's described here.
 * Mirrors the sibling `FILTER_SERIALIZERS` registry.
 */
export const FILTER_DESCRIBERS: Record<FilterValue['kind'], FilterDescriber> = {
  text: ((v: TextFilterValue) => `« ${v.value} »`) as FilterDescriber,
  date: ((v: DateFilterValue) =>
    `${OP_SYMBOL[v.op]} ${formatDate(v.value, DATE_FORMAT)}`) as FilterDescriber,
  dateRange: ((v: DateRangeFilterValue) => describeDateRange(v)) as FilterDescriber,
  numeric: ((v: NumericFilterValue) => `${OP_SYMBOL[v.op]} ${v.value}`) as FilterDescriber,
  boolean: ((v: BooleanFilterValue, { t }: DescribeContext) =>
    v.value ? t('table.filter.yes') : t('table.filter.no')) as FilterDescriber,
  monthYear: ((v: MonthYearFilterValue) => v.value) as FilterDescriber,
  list: describeList as FilterDescriber,
};

/** Describe a single filter value as "op value(s)" (no field label). */
function describeValue(col: TableColumn<unknown>, value: FilterValue, t: TFunction): string {
  return FILTER_DESCRIBERS[value.kind](value, { col, t });
}

/**
 * Build the removable "active criteria" chips for a set of filter keys — agnostic
 * of the feature. Each chip carries its `key` so the caller can clear exactly that
 * filter on removal. Keys with no value in `state` are skipped.
 */
export function buildFilterChips<T>(
  keys: (keyof T)[],
  allColumns: TableColumn<T>[],
  state: FilterState<T>,
  t: TFunction,
): FilterChip<T>[] {
  const chips: FilterChip<T>[] = [];
  for (const key of keys) {
    const value = state[key];
    if (value === undefined) continue;
    const col = allColumns.find(c => c.key === key);
    if (!col) continue;
    const label = t(col.labelKey);
    const desc = describeValue(col as TableColumn<unknown>, value, t);
    chips.push({ key, text: desc ? `${label} ${desc}` : label });
  }
  return chips;
}
